import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Project } from "@/lib/api-types";

const PROJECTS_KEY = ["projects"] as const;

/**
 * GET /api/editor/projects — works for any workspace role, scoped to the
 * workspace. `enabled` defaults true; pass false to skip the request (e.g.
 * TopBar's global search — superadmin sessions aren't workspace members and
 * this endpoint would just 403 for them).
 */
export function useProjects(enabled = true) {
  return useQuery<Project[]>({
    queryKey: PROJECTS_KEY,
    queryFn: () => api.get<Project[]>("/editor/projects"),
    staleTime: 30_000,
    enabled,
  });
}

/** GET /api/editor/projects/:id — single project. 404s if it's not in your workspace. */
export function useProject(id: string | undefined) {
  return useQuery<Project>({
    queryKey: [...PROJECTS_KEY, id],
    queryFn: () => api.get<Project>(`/editor/projects/${id}`),
    enabled: !!id,
  });
}

/**
 * POST /api/editor/projects — create a project. Backend gates this to
 * ADMIN/FULL_ACCESS via ACTIONS.CREATE_PROJECT; other roles get a 403.
 * Gate the "New Project" button client-side with canCreateProject() too so
 * we don't show a control that will just fail.
 */
export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api.post<{ ok: true; id: string }>("/editor/projects", { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROJECTS_KEY });
    },
  });
}

export type CoverAction =
  | { type: "set"; blob: Blob }
  | { type: "clear" };

export type UpdateProjectPayload = {
  id: string;
  name?: string;
  /** null = retention off. Backend only accepts null | 15 | 30 | 60. */
  retentionDays?: number | null;
  /**
   * Cover image change — locked 2026-07-13, migration 0010.
   *   { type: "set", blob }  → presign → PUT blob to R2 → PATCH with new key + bytes.
   *   { type: "clear" }      → PATCH with { coverImageKey: null }; server deletes the R2 blob.
   *   omitted                → no cover change; existing key + bytes preserved.
   */
  cover?: CoverAction;
  /**
   * Per-project restriction — locked 2026-07-14, migration 0011.
   * Flipping true wipes EDIT / EDIT_SHARE access instantly; those users must
   * be re-invited via useInviteProjectMember. Omitted = no change.
   */
  restricted?: boolean;
};

/**
 * PATCH /api/editor/projects/:id — rename, change retention, and/or swap the
 * cover image. Backend gates this on ACTIONS.CREATE_PROJECT (same roles as
 * create/delete — see canDeleteProject() in api-types.ts, same role set today).
 *
 * When `cover.type === "set"`, the mutation runs a THREE-step flow:
 *   1. POST /projects/:id/cover/presign → { url, key }
 *   2. Browser PUT blob directly to R2 at `url` with matching Content-Type.
 *   3. PATCH /projects/:id with { ...patch, coverImageKey, coverImageBytes }.
 * If step 3 fails, step 2's R2 object is orphaned. Acceptable — the client
 * retry re-presigns to a fresh key (nanoid path), and the old orphan is
 * never referenced by any project row. R2 orphan sweep out of scope for
 * this pass.
 */
export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, cover, ...patch }: UpdateProjectPayload) => {
      const body: Record<string, unknown> = { ...patch };
      // `restricted` already lives in `patch` via the spread — no special casing.

      if (cover?.type === "set") {
        // Client always crops to JPEG (see project-cover-image-dialog). If we ever
        // let raw uploads through, thread the real MIME through here.
        const contentType = cover.blob.type || "image/jpeg";
        const contentLength = cover.blob.size;

        const presign = await api.post<{ url: string; key: string; expiresIn: number }>(
          `/editor/projects/${id}/cover/presign`,
          { contentType, contentLength },
        );

        // Direct PUT to R2. Content-Type MUST match what was signed or R2
        // returns SignatureDoesNotMatch (see worker/lib/video.js note).
        const putRes = await fetch(presign.url, {
          method: "PUT",
          headers: { "Content-Type": contentType },
          body: cover.blob,
        });
        if (!putRes.ok) {
          throw new Error(`Cover image upload to R2 failed (${putRes.status})`);
        }

        body.coverImageKey = presign.key;
        body.coverImageBytes = contentLength;
      } else if (cover?.type === "clear") {
        body.coverImageKey = null;
      }

      return api.patch<{ ok: true; coverImageUrl?: string | null }>(
        `/editor/projects/${id}`,
        body,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROJECTS_KEY });
    },
  });
}

/**
 * PATCH /api/editor/projects/:id/status — mark a project active or inactive.
 * DISPLAY-flag only (locked 2026-07-12) — nothing else in the backend gates
 * on this. Backend permission: ADMIN + FULL_ACCESS (mirrors DELETE_PROJECT).
 */
export function useUpdateProjectStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch<{ ok: true; id: string; inactiveAt: string | null }>(
        `/editor/projects/${id}/status`,
        { active },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROJECTS_KEY });
    },
  });
}

/**
 * DELETE /api/editor/projects/:id — SOFT delete (behavior changed 2026-07-08).
 * Moves project to trash for 10 days and cascade-soft-deletes all currently-live
 * videos in the project with deletionType='cascade_from_project'.
 * Individually-trashed videos are UNTOUCHED. Restore via POST .../:id/restore.
 * Gated ADMIN/FULL_ACCESS — see canDeleteProject().
 */
export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/editor/projects/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROJECTS_KEY });
    },
  });
}

/**
 * DELETE /api/editor/projects/:id/permanent — PERMANENT cascade. Nukes ALL
 * videos in the project (live + individually-trashed) via deleteVideoCascade,
 * plus uploadSessions + projectEditors rows + project row itself.
 * State-agnostic — works on live projects AND trashed projects (relaxed
 * 2026-07-08 — type-to-confirm UI gate is the safeguard now). Cannot be undone.
 */
export function useDeleteProjectPermanent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: true }>(`/editor/projects/${id}/permanent`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROJECTS_KEY });
      qc.invalidateQueries({ queryKey: ["trash"] });
    },
  });
}

/**
 * POST /api/editor/projects/:id/restore — un-trash a project. Clears project
 * trash cols AND clears trash cols on videos that were cascade-marked
 * (deletionType='cascade_from_project'). Individually-trashed videos are
 * NOT restored — they keep their own trash timeline.
 */
export function useRestoreProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: true }>(`/editor/projects/${id}/restore`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROJECTS_KEY });
      qc.invalidateQueries({ queryKey: ["trash"] });
    },
  });
}
